import React, { useEffect, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { KpiCard, StatusPill } from '../../components/admin/KpiCard';
import { adminApi } from '../../lib/api';
import { formatCurrency, formatDateTime, getCheckInStatusClass, getPaymentStatusClass, CHECK_IN_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../../lib/utils';
import { toast } from 'sonner';
import { Users, CreditCard, UserCheck, TrendingUp, Clock, AlertCircle, CheckCircle2, Calendar, UsersRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    adminApi.getDashboard()
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const today = data?.today || {};
  const yesterday = data?.yesterday || {};
  const recent = data?.recent_checkins || [];
  const customers = data?.customers || {};
  const events = data?.events || {};
  const recentCustomers = data?.recent_customers || [];

  const revenueChange = yesterday.total_revenue
    ? `${today.total_revenue > yesterday.total_revenue ? '+' : ''}${Math.round(((today.total_revenue - yesterday.total_revenue) / yesterday.total_revenue) * 100)}% vs yesterday`
    : null;

  return (
    <AdminShell title="Dashboard">
      <div className="space-y-6">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Today's Check-ins"
            value={loading ? '—' : today.total_checkins}
            subtitle={`${today.checked_in || 0} checked in`}
            icon={UserCheck}
            loading={loading}
            className="animate-fade-in-up"
            style={{ animationDelay: '0ms' }}
          />
          <KpiCard
            title="Total Guests"
            value={loading ? '—' : today.total_guests}
            subtitle="today"
            icon={Users}
            loading={loading}
            className="animate-fade-in-up"
          />
          <KpiCard
            title="Revenue Today"
            value={loading ? '—' : today.total_revenue}
            subtitle={revenueChange}
            icon={TrendingUp}
            trend={today.total_revenue > yesterday.total_revenue ? 'up' : today.total_revenue < yesterday.total_revenue ? 'down' : null}
            trendValue={revenueChange}
            loading={loading}
          />
          <KpiCard
            title="Paid / Pending"
            value={loading ? '—' : `${today.paid_checkins || 0} / ${today.pending_checkins || 0}`}
            subtitle={`${today.no_shows || 0} no-shows`}
            icon={CreditCard}
            loading={loading}
          />
        </div>

        {/* Customer & Events KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Total Customers"
            value={loading ? '—' : customers.total || 0}
            subtitle={`${customers.new_this_week || 0} new this week`}
            icon={UsersRound}
            loading={loading}
            onClick={() => navigate('/admin/customers')}
            className="cursor-pointer hover:scale-[1.02] transition-transform"
            data-testid="dashboard-total-customers-card"
          />
          <KpiCard
            title="Active Events"
            value={loading ? '—' : events.active_upcoming || 0}
            subtitle="upcoming events"
            icon={Calendar}
            loading={loading}
            onClick={() => navigate('/admin/events')}
            className="cursor-pointer hover:scale-[1.02] transition-transform"
            data-testid="dashboard-active-events-card"
          />
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Paid', value: today.paid_checkins, cls: 'status-paid' },
            { label: 'Pending', value: today.pending_checkins, cls: 'status-pending' },
            { label: 'Checked In', value: today.checked_in, cls: 'status-checked_in' },
            { label: 'No Shows', value: today.no_shows, cls: 'status-no_show' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl p-3 text-center ${s.cls}`}>
              <p className="text-lg font-['EB_Garamond'] font-semibold">{loading ? '—' : (s.value || 0)}</p>
              <p className="text-xs">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="surface-card card-highlight rounded-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
            <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Recent Check-ins</h2>
            <Button
              variant="outline"
              size="sm"
              className="text-xs bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40"
              onClick={() => navigate('/admin/checkins')}
            >
              View all
            </Button>
          </div>
          <div className="divide-y divide-[#1E1E1E]">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-5 py-3 flex gap-3 items-center">
                  <div className="h-8 w-8 bg-[#202020] rounded-full animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 bg-[#202020] rounded animate-pulse" />
                    <div className="h-2 w-20 bg-[#1A1A1A] rounded animate-pulse" />
                  </div>
                </div>
              ))
            ) : recent.length === 0 ? (
              <div className="px-5 py-8 text-center text-[#6F6F6F] text-sm">No check-ins yet today</div>
            ) : (
              recent.slice(0, 8).map(ci => (
                <div
                  key={ci._id}
                  className="px-5 py-3 flex items-center gap-3 hover:bg-[#161616] cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/checkins/${ci._id}`)}
                >
                  <div className="w-8 h-8 rounded-full bg-[#D4A843]/15 border border-[#D4A843]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D4A843] text-xs font-semibold">{ci.customer_name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#F2F2F2] font-medium truncate">{ci.customer_name}</p>
                    <p className="text-xs text-[#B9B9B9]">{ci.phone} • {ci.guests} guest{ci.guests > 1 ? 's' : ''} • {ci.visit_date}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getPaymentStatusClass(ci.payment_status)}`}>
                      {PAYMENT_STATUS_LABELS[ci.payment_status] || ci.payment_status}
                    </span>
                    <span className="text-xs text-[#D4A843]">{formatCurrency(ci.total_amount)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
